'use client'

import React, { useEffect, useRef, useState } from 'react'
import { cn } from '@/utilities/ui'

/**
 * Scroll-reveal hook: returns a ref to attach and an `inView` flag that flips
 * true once (and stays true) when the element enters the viewport. Mirrors the
 * IntersectionObserver pattern used by AnimatedList.
 */
export function useInView<T extends HTMLElement = HTMLDivElement>(threshold = 0.2) {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [threshold])

  return { ref, inView }
}

type RevealProps = {
  children: React.ReactNode
  className?: string
  /** Index used to stagger sibling reveals. */
  index?: number
  /** Per-item stagger in ms (capped). */
  stagger?: number
  as?: 'div' | 'li' | 'section'
}

const STAGGER_CAP = 6

/**
 * Fade + rise on scroll, with optional staggered delay. Fully disabled under
 * prefers-reduced-motion.
 */
export const Reveal: React.FC<RevealProps> = ({
  children,
  className,
  index = 0,
  stagger = 70,
  as = 'div',
}) => {
  const { ref, inView } = useInView<HTMLDivElement>()
  const Tag = as as React.ElementType

  return (
    <Tag
      ref={ref}
      style={{
        transitionDelay: inView ? `${Math.min(index, STAGGER_CAP) * stagger}ms` : '0ms',
      }}
      className={cn(
        'transition-[opacity,transform] duration-700 ease-out',
        'motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0',
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
        className,
      )}
    >
      {children}
    </Tag>
  )
}
